import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Zap, CheckCircle2, MapPin, Monitor } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, inr, track } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useCurrency } from "../context/CurrencyContext";
import { APP_TYPES } from "../data/content";
import { toast } from "../components/ui/sonner";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold tracking-wider text-slate-400 mb-2">{label}</label>
    {children}
  </div>
);

export default function BookRushHour() {
  const [params] = useSearchParams();
  const { settings } = useSite();
  const { money } = useCurrency();
  const r = settings.rates;
  const initialSpecialist = params.get("specialist") === "super" ? "super" : "specialist";

  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", requirement: "", application_type: "",
    expected_features: "", specialist: initialSpecialist, preferred_date: "",
    preferred_start_time: "", expected_hours: "", mode: "remote",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/rush-hour-bookings", form);
      track("rush_hour_submitted", { specialist: form.specialist });
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Request failed. Please try again or WhatsApp us.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-32" data-testid="rush-booking-success">
        <div className="container-x max-w-2xl text-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
          <h1 className="mt-8 font-heading text-4xl sm:text-5xl font-black tracking-tight text-white">REQUEST RECEIVED.</h1>
          <p className="mt-6 text-slate-400 leading-relaxed">
            Rush Hour sessions are confirmed after requirement review, availability confirmation and payment.
            Our team will contact you to review scope and schedule.
          </p>
          <p className="mt-8 font-mono text-[11px] tracking-[0.3em] text-slate-600">REQUEST → SCOPE REVIEW → AVAILABILITY → ESTIMATE → PAYMENT → CONFIRMED</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 lg:pt-40 pb-24" data-testid="book-rush-hour-page">
      <div className="container-x max-w-4xl">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse-dot" />
          <p className="section-label">RUSH HOUR LIVE BUILD</p>
        </div>
        <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white">
          BOOK <span className="text-flame">RUSH HOUR.</span>
        </h1>
        <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
          Tell us what you need built live. We review scope, confirm availability and send your estimate before the
          session is confirmed.
        </p>

        <form onSubmit={submit} className="mt-14 rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-12 space-y-10" data-testid="rush-booking-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="NAME *"><Input required value={form.name} onChange={set("name")} data-testid="rush-name" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="COMPANY"><Input value={form.company} onChange={set("company")} data-testid="rush-company" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="EMAIL *"><Input required type="email" value={form.email} onChange={set("email")} data-testid="rush-email" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="PHONE"><Input value={form.phone} onChange={set("phone")} data-testid="rush-phone" className="bg-navy-900/60 border-white/10 h-12" /></Field>
          </div>

          <Field label="APPLICATION REQUIREMENT *">
            <Textarea required rows={4} value={form.requirement} onChange={set("requirement")} data-testid="rush-requirement" placeholder="Describe what should be built live." className="bg-navy-900/60 border-white/10" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="APPLICATION TYPE">
              <Select value={form.application_type} onValueChange={set("application_type")}>
                <SelectTrigger data-testid="rush-app-type" className="bg-navy-900/60 border-white/10 h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-navy-800 border-white/10">{APP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="EXPECTED HOURS"><Input value={form.expected_hours} onChange={set("expected_hours")} data-testid="rush-hours" placeholder="e.g. 4" className="bg-navy-900/60 border-white/10 h-12" /></Field>
          </div>

          <Field label="KEY FEATURES">
            <Textarea rows={3} value={form.expected_features} onChange={set("expected_features")} data-testid="rush-features" placeholder="The features that must be working by end of session." className="bg-navy-900/60 border-white/10" />
          </Field>

          <div>
            <label className="block text-xs font-semibold tracking-wider text-slate-400 mb-3">PREFERRED SPECIALIST *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: "specialist", name: "VIBE CODING SPECIALIST", rate: r.specialist, desc: "MVPs, dashboards, internal tools, portals, automation" },
                { key: "super", name: "SUPER VIBE CODER", rate: r.super, desc: "Critical projects, enterprise prototypes, executive sessions" },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`rush-specialist-${s.key}`}
                  onClick={() => setForm({ ...form, specialist: s.key })}
                  className={`text-left rounded-xl border p-6 transition-colors ${form.specialist === s.key ? "border-flame bg-flame/10" : "border-white/10 bg-navy-900/50 hover:border-white/25"}`}
                >
                  <p className="font-heading font-bold text-sm text-white">{s.name}</p>
                  <p className="mt-2 font-heading text-2xl font-black text-flame">{money(s.rate)}<span className="text-xs text-slate-400">/hour</span></p>
                  <p className="mt-2 text-[11px] text-slate-400">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="PREFERRED DATE"><Input type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="rush-date" className="bg-navy-900/60 border-white/10 h-12" /></Field>
            <Field label="PREFERRED START TIME"><Input type="time" value={form.preferred_start_time} onChange={set("preferred_start_time")} data-testid="rush-time" className="bg-navy-900/60 border-white/10 h-12" /></Field>
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-wider text-slate-400 mb-3">REMOTE / ON-SITE</label>
            <div className="flex gap-4">
              {[
                { key: "remote", label: "REMOTE", icon: Monitor },
                { key: "onsite", label: "ON-SITE", icon: MapPin },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  data-testid={`rush-mode-${m.key}`}
                  onClick={() => setForm({ ...form, mode: m.key })}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-6 py-4 font-heading font-bold text-sm transition-colors ${form.mode === m.key ? "border-flame bg-flame/10 text-flame" : "border-white/10 bg-navy-900/50 text-slate-300 hover:border-white/25"}`}
                >
                  <m.icon className="w-4 h-4" /> {m.label}
                </button>
              ))}
            </div>
            {form.mode === "onsite" && (
              <p className="mt-3 text-[11px] text-slate-500">On-site sessions: travel and applicable logistics charges may be additional.</p>
            )}
          </div>

          <Button type="submit" disabled={busy} data-testid="rush-submit-btn" className="w-full bg-flame hover:bg-flame-hover text-white font-heading font-bold h-14 rounded-full text-base">
            <Zap className="w-5 h-5" /> {busy ? "SUBMITTING…" : "REQUEST RUSH HOUR"}
          </Button>
          <p className="text-center text-[11px] text-slate-600">
            Bookings are not auto-confirmed. Sessions are confirmed after requirement review, availability confirmation and payment.
          </p>
        </form>
      </div>
    </div>
  );
}
